import { z } from 'zod';

const VALUES_SEPARATOR = '|';

/**
 * An i18n key with its interpolation values, as the one string a zod issue and a
 * React Hook Form error can carry: `form.errors.maxLength|{"max":1000}`.
 * `ErrorMessage` splits it back with `parseMessage` and calls `t(key, values)`,
 * so the limit a schema states is the one the user reads.
 */
export const messageWithValues = (key: string, values: Record<string, string | number>): string =>
  `${key}${VALUES_SEPARATOR}${JSON.stringify(values)}`;

/** The inverse of `messageWithValues`; a plain key or a ready string comes back as the key. */
export const parseMessage = (message: string): { key: string; values?: Record<string, string | number> } => {
  const at = message.indexOf(`${VALUES_SEPARATOR}{`);
  if (at === -1) {
    return { key: message };
  }
  try {
    return { key: message.slice(0, at), values: JSON.parse(message.slice(at + 1)) as Record<string, string | number> };
  } catch {
    return { key: message };
  }
};

/*
 * This function provides a custom way to handle errors from zod.
 * It allows us to create custom error messages for each type of error,
 * so that we can then translate them in the ErrorMessage component (or elsewhere).
 * Official documentation : https://zod.dev/ERROR_HANDLING
 * */
export const customErrorMap: z.ZodErrorMap = (issue, _) => {
  if (issue.code === z.ZodIssueCode.invalid_string) {
    switch (issue.validation) {
      case 'email':
        return { message: 'form.errors.format-email' };
      case 'url':
        return { message: 'form.errors.format-url' };
      case 'uuid':
        return { message: 'form.errors.format-uuid' };
      default:
        return { message: 'form.errors.generic' };
    }
  }
  if (issue.code === z.ZodIssueCode.invalid_type) {
    if (issue.received === 'undefined' || issue.received === 'null') {
      return { message: 'form.errors.required' };
    }

    switch (issue.expected) {
      case 'string':
        return { message: 'form.errors.format-string' };
      case 'number':
        return { message: 'form.errors.format-number' };
      case 'date':
        return { message: 'form.errors.format-date' };
      default:
        return { message: 'form.errors.generic' };
    }
  }
  if (issue.code === z.ZodIssueCode.invalid_enum_value) {
    // A segment or select cleared to '' on a required enum: what is missing is a
    // choice. Anything else is a value outside the enum (a stored record the
    // list no longer knows), reported on its field rather than dropped.
    return { message: issue.received === '' ? 'form.errors.required' : 'form.errors.format-string' };
  }
  if (issue.code === z.ZodIssueCode.too_big && issue.type === 'string') {
    // The cap comes from the schema (`.max(n)`); the message carries it to the user.
    return { message: messageWithValues('form.errors.maxLength', { max: Number(issue.maximum) }) };
  }
  if (issue.code === z.ZodIssueCode.custom) {
    return { message: 'generic-error' };
  }

  return { message: 'generic-error' };
};
